import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesign, isFeaturePatternTruss, isProfileTruss } from '@paragon/contract-library';
import { naturalSortByProperty, notNullish, sortBy } from '@paragon/utilities';

import { RootState } from '..';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getActiveStation } from './getActiveStation';
import { getCommittedComponentDesigns } from './getComponentDesignsSelectors';

const getStationSortBy = createSelector(
  getCommittedComponentDesigns,
  getActiveStation,
  (componentDesigns, station): string[] | null => {
    if (station && componentDesigns.some((design) => station.componentDesigns[design.guid]?.index != null)) {
      const guidsAndIndexes = Object.entries(station.componentDesigns).map(([guid, { index }]) => ({ guid, index }));
      return sortBy(guidsAndIndexes, ({ index }) => index ?? Infinity)
        .map(({ guid }) => {
          const possibleComponentDesign = componentDesigns.find((design) => design.guid === guid);
          return possibleComponentDesign ? possibleComponentDesign.component.name : null;
        })
        .filter(notNullish);
    }

    return null;
  },
);

export const getApplicableSortBy = createSelector(
  getStationSortBy,
  (state: RootState) => state.application.componentDesignSortBy,
  (stationSortBy, applicationSortBy) => stationSortBy ?? applicationSortBy,
);

// There are many selectors for getting a list of component designs, each with subtly different behavior.  For a
//   complete list, see the guide in getComponentDesignsSelectors.ts

export const getSortedComponentDesigns = createSelector(
  getCommittedComponentDesigns,
  getActiveComponentDesign,
  getApplicableSortBy,

  (componentDesigns, activeComponentDesign, applicableSortBy) => {
    const componentDesignsToDisplay = activeComponentDesign
      ? componentDesigns.map((componentDesign) =>
          componentDesign.guid === activeComponentDesign.guid ? activeComponentDesign : componentDesign,
        )
      : componentDesigns;

    if (Array.isArray(applicableSortBy)) {
      return sortBy(componentDesignsToDisplay, (design) => applicableSortBy.indexOf(design.component.name));
    }

    if (applicableSortBy === 'name') {
      return naturalSortByProperty(componentDesignsToDisplay, (componentDesign) => componentDesign.component.name);
    }

    throw new Error('Unsupported sorting type.');
  },
);

/**
 * Used in NavigationPanel. Check if you should use @see getSortedComponentDesigns first.
 */
export const getSortedComponentDesignsForNavigationPanel = createSelector(
  getSortedComponentDesigns,
  (componentDesigns) => componentDesigns.map(formatComponentDesignForCustomSchemas),
);

export const getNaturalSortedComponentDesigns = createSelector(getCommittedComponentDesigns, (componentDesigns) =>
  naturalSortByProperty(componentDesigns, (componentDesign) => componentDesign.component.name),
);

export const formatComponentDesignForCustomSchemas = createSelector(
  [(componentDesign: ComponentDesign) => componentDesign],
  (componentDesign) => {
    let componentDesignType = 'componentDesign';
    if (isProfileTruss(componentDesign.component)) {
      componentDesignType = 'profileTrussDesign';
    } else if (isFeaturePatternTruss(componentDesign.component)) {
      componentDesignType = 'featurePatternTrussDesign';
    }

    return { ...componentDesign, type: componentDesignType };
  },
);
